import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navigation = {
    main: [
      {
        name: 'About Us',
        href: '/about',
        dropdown: [
          { name: 'Company Overview', href: '/about' },
          { name: 'Leadership Team', href: '/leadership' }
        ]
      },
      {
        name: 'Services',
        href: '/services',
        dropdown: [
          { name: 'Managed IT', href: '/services/managed-it' },
          { name: 'Co-Managed IT', href: '/services/co-managed-it' },
          { name: 'Professional Services', href: '/services/professional-services' },
          { name: 'Consulting Services', href: '/services/consulting' }
        ]
      },
      { name: 'Contact', href: '/contact' },
      { name: 'Careers', href: '/careers' }
    ],
    customer: {
      name: 'Customer Portal',
      links: [
        { name: 'Monitoring Portal', href: 'https://ucx.logicmonitor.com', external: true },
        { name: 'Remote Support', href: '/remote-support' }
      ]
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isActiveDropdown = (items: any[]) => items.some(item => location.pathname === item.href);

  return (
    <nav className="bg-white shadow-lg fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center" onClick={() => setActiveDropdown(null)}>
              <img 
                src="https://i.ibb.co/QpjHXgM/UCXLogo.png" 
                alt="UCX Logo" 
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {/* Main Navigation Items */}
            {navigation.main.map((item) => (
              <div
                key={item.name}
                className="relative group"
              >
                <Link
                  to={item.href}
                  className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.href) || (item.dropdown && isActiveDropdown(item.dropdown))
                      ? 'text-blue-600'
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                >
                  {item.name}
                  {item.dropdown && (
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform group-hover:rotate-180`} />
                  )}
                </Link>

                {/* Dropdown Menu */}
                {item.dropdown && (
                  <div className="absolute left-0 mt-0 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pt-2">
                    <div className="bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`block px-4 py-2 text-sm ${
                            isActive(subItem.href)
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Customer Portal Button */}
            <div className="relative group">
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center">
                {navigation.customer.name}
                <ChevronDown className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>

              <div className="absolute right-0 mt-0 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pt-2">
                <div className="bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                  {navigation.customer.links.map((link) => (
                    link.external ? (
                      <a
                        key={link.name}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                      >
                        {link.name}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    ) : (
                      <Link
                        key={link.name}
                        to={link.href}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                      >
                        {link.name}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.main.map((item) => (
              <div key={item.name} className="space-y-1">
                <Link
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium rounded-md ${
                    isActive(item.href)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {item.name}
                </Link>
                {item.dropdown && (
                  <div className="pl-4 space-y-1">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className={`block px-3 py-2 text-sm rounded-md ${
                          isActive(subItem.href)
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile Customer Portal Links */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <div className="px-3 py-2 text-base font-medium text-gray-700">
                {navigation.customer.name}
              </div>
              <div className="pl-4 space-y-1">
                {navigation.customer.links.map((link) => (
                  link.external ? (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                    >
                      {link.name}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.href}
                      className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                    >
                      {link.name}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}